// The notification bell's feed. See docs/plans/notifications-redesign.md for
// the item and section JSON.

import {
	fetchNotificationPage,
	fetchNotificationsSection,
	postAllNotificationsRead,
	postNotificationsRead,
	postNotificationsToasted,
} from 'react/notifications/notificationRequests';
import { isNetplayPageActive } from 'react/netplay/netplayPageState';

export const NOTIFICATIONS_LOADED = 'NOTIFICATIONS_LOADED';
export const NOTIFICATION_PUSHED = 'NOTIFICATION_PUSHED';
export const NOTIFICATIONS_MARKED_READ = 'NOTIFICATIONS_MARKED_READ';
export const NOTIFICATIONS_REMOVED = 'NOTIFICATIONS_REMOVED';
export const NOTIFICATION_PAGE_APPENDED = 'NOTIFICATION_PAGE_APPENDED';
export const NOTIFICATION_PAGE_LOADING = 'NOTIFICATION_PAGE_LOADING';
export const NOTIFICATION_TOAST_ENQUEUED = 'NOTIFICATION_TOAST_ENQUEUED';
export const NOTIFICATION_TOAST_DEQUEUED = 'NOTIFICATION_TOAST_DEQUEUED';
export const NOTIFICATION_INVITE_ANSWERED = 'NOTIFICATION_INVITE_ANSWERED';
export const DIRECT_MESSAGE_RECEIVED = 'DIRECT_MESSAGE_RECEIVED';

export const ACHIEVEMENT_KIND = 'achievement';

export const notificationsLoaded = (section) => ({ type: NOTIFICATIONS_LOADED, payload: section });
export const notificationPushed = ({ item, unread_count: unreadCount }) => ({ type: NOTIFICATION_PUSHED, payload: { item, unreadCount } });
export const notificationsMarkedRead = ({ ids = [], all = false, unreadCount = null }) => ({ type: NOTIFICATIONS_MARKED_READ, payload: { ids, all, unreadCount } });
export const notificationsRemoved = ({ ids = [], unreadCount = null }) => ({ type: NOTIFICATIONS_REMOVED, payload: { ids, unreadCount } });
export const notificationPageAppended = ({ items, has_more: hasMore }) => ({ type: NOTIFICATION_PAGE_APPENDED, payload: { items, hasMore } });
export const notificationPageLoading = (loading) => ({ type: NOTIFICATION_PAGE_LOADING, payload: loading });
export const notificationToastEnqueued = (item) => ({ type: NOTIFICATION_TOAST_ENQUEUED, payload: item });
export const notificationToastDequeued = (id) => ({ type: NOTIFICATION_TOAST_DEQUEUED, payload: id });
export const notificationInviteAnswered = (id) => ({ type: NOTIFICATION_INVITE_ANSWERED, payload: id });
// One of react/notifications/directMessagePushes: { sender: {id, username}, preview, sentAt }.
export const directMessageReceived = (push) => ({ type: DIRECT_MESSAGE_RECEIVED, payload: push });

function unreadCountFrom(response) {
	return response && Number.isFinite(Number(response.unread_count)) ? Number(response.unread_count) : null;
}

export const refreshNotifications = () => async (dispatch) => {
	const section = await fetchNotificationsSection();
	if(section) {
		dispatch(notificationsLoaded(section));
	}
};

export const loadOlderNotifications = () => async (dispatch, getState) => {
	const { items, loadingMore } = getState().notifications;
	if(loadingMore) {
		return;
	}
	const oldestItem = items[items.length - 1];
	dispatch(notificationPageLoading(true));
	try {
		const page = await fetchNotificationPage(oldestItem ? oldestItem.id : null);
		dispatch(notificationPageAppended({ items: page.items || [], has_more: !!page.has_more }));
	} finally {
		dispatch(notificationPageLoading(false));
	}
};

export const markNotificationRead = (id) => async (dispatch, getState) => {
	const item = getState().notifications.items.find((candidate) => candidate.id === id);
	if(!item || item.read) {
		return;
	}
	dispatch(notificationsMarkedRead({ ids: [id] }));
	const response = await postNotificationsRead([id]);
	const unreadCount = unreadCountFrom(response);
	if(unreadCount !== null) {
		dispatch(notificationsMarkedRead({ ids: [], unreadCount }));
	}
};

export const markAllNotificationsRead = () => async (dispatch) => {
	dispatch(notificationsMarkedRead({ all: true }));
	const response = await postAllNotificationsRead();
	const unreadCount = unreadCountFrom(response);
	if(unreadCount !== null) {
		dispatch(notificationsMarkedRead({ ids: [], unreadCount }));
	}
};

// A socket push: {item, unread_count}. Achievements also pop up as a toast.
export const receivedNotificationPush = (push) => (dispatch) => {
	if(!push || !push.item) {
		return;
	}
	dispatch(notificationPushed(push));
	if(push.item.kind === ACHIEVEMENT_KIND) {
		dispatch(notificationToastEnqueued(push.item));
	}
};

// The server marked items read for the player, as when reading a room marks
// its mentions read: {ids, unread_count}.
export const receivedNotificationsRead = (push) => (dispatch) => {
	if(!push || !Array.isArray(push.ids)) {
		return;
	}
	dispatch(notificationsMarkedRead({ ids: push.ids, unreadCount: push.unread_count }));
};

// The server deleted items, as when a mentioning message is deleted: {ids, unread_count}.
export const receivedNotificationsRemoved = (push) => (dispatch) => {
	if(!push || !Array.isArray(push.ids)) {
		return;
	}
	dispatch(notificationsRemoved({ ids: push.ids, unreadCount: push.unread_count }));
};

// Direct messages the site socket pushed (react/notifications/directMessagePushes).
// The section lists at most five unread conversations: a sender it lists, or
// any sender when it lists them all, is applied here; otherwise the sender may
// already be one of the unlisted ones, so the section is reloaded instead.
export const receivedDirectMessages = (pushes) => (dispatch, getState) => {
	let needsReload = false;
	(pushes || []).forEach((push) => {
		const { loaded, conversations, unreadConversationCount } = getState().notifications;
		if(!loaded) {
			return;
		}
		const listed = conversations.some((conversation) => conversation.player.id === push.sender.id);
		if(listed || unreadConversationCount <= conversations.length) {
			dispatch(directMessageReceived(push));
		}
		else {
			needsReload = true;
		}
	});
	if(needsReload) {
		return dispatch(refreshNotifications());
	}
	return Promise.resolve();
};

export const finishNotificationToast = (id) => (dispatch) => {
	dispatch(notificationToastDequeued(id));
	return Promise.resolve(postNotificationsToasted([id])).catch((error) => {
		console.error('Could not mark the toast as shown', error);
	});
};

// On the netplay page the direct message count is live in privateChat;
// elsewhere it comes from the notifications section.
export function selectUnreadConversationCount(state) {
	if(isNetplayPageActive() && state.privateChat) {
		return Number(state.privateChat.unreadPrivateMessageCount) || 0;
	}
	return state.notifications.unreadConversationCount;
}

export function selectBellUnreadCount(state) {
	return state.notifications.unreadCount + selectUnreadConversationCount(state);
}
